import React, { useState, useMemo } from "react";
import { useIsMobile } from "../../../hooks/use-mobile";
import { Card, CardContent } from "../../../components/ui/card";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { cn } from "../../../lib/utils";
import { getIntegrationConfig } from "../../../config/integrationConfig";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../../components/ui/collapsible";
import { ChevronDown } from "lucide-react";

// Normalize provider name to match integration config keys
const normalizeProviderName = (providerName) => {
  if (!providerName) return null;

  const normalized = providerName.toLowerCase().trim();

  // Map common variations to integration config keys
  const nameMapping = {
    // Repository integrations
    github: "github",
    gitlab: "gitlab",
    "git lab": "gitlab",
    bitbucket: "bitbucket",
    "bit bucket": "bitbucket",
    gitea: "gitea",

    // Storage integrations
    s3: "s3",
    "aws s3": "s3",
    gcs: "gcs",
    "google cloud storage": "gcs",
    googlecloudstorage: "gcs",
    "azure blob": "azure-blob",
    azureblob: "azure-blob",
    "azure blob storage": "azure-blob",
    "microsoft azure blob storage": "azure-blob",

    // Cloud Infrastructure
    aws: "aws",
    gcp: "gcp",
    "google cloud platform": "gcp",
    googlecloudplatform: "gcp",
    azure: "azure",
    "microsoft azure": "azure",

    // AI/LLM providers
    openai: "openai",
    anthropic: "anthropic",
    claude: "anthropic",
    groq: "groq",
    huggingface: "huggingface",
    "hugging face": "huggingface",

    // Other services
    atlassian: "atlassian",
    jira: "atlassian",
    postman: "postman",
    sendgrid: "sendgrid",
    "send grid": "sendgrid",
    slack: "slack",
    slackwebhook: "slack",
    "microsoft teams": "teams",
    teams: "teams",
    msteams: "teams",
  };

  return nameMapping[normalized] || normalized;
};

// Icon mapping for providers - uses integration configs as source of truth
const getProviderIcon = (providerName) => {
  if (!providerName) return "/github.png";

  // First, try to get icon from integration config
  const normalizedName = normalizeProviderName(providerName);
  if (normalizedName) {
    const config = getIntegrationConfig(normalizedName);
    if (config && config.icon) {
      return config.icon;
    }
  }

  // Fallback mapping for providers not in integration configs
  const fallbackIconMap = {
    GitHub: "/github.png",
    Github: "/github.png",
    GitLab: "/gitlab.svg",
    Gitlab: "/gitlab.svg",
    Bitbucket: "/bitbucket.png",
    Gitea: "/inte/gitea.png",
    S3: "/s3.png",
    AWS: "/settingspage/aws.svg.png",
    Azure: "/inte/Azure.png",
    "Azure Blob": "/inte/AzureBlockStorage.png",
    GCP: "/inte/GCP.png",
    Gcp: "/inte/GCP.png",
    Gcs: "/inte/GoogleCloudStorage.svg",
    "Google Cloud Storage": "/inte/GoogleCloudStorage.svg",
    OpenAI: "/ai-logos/openai.webp",
    Anthropic: "/ai-logos/anthropic.webp",
    Groq: "/ai-logos/groq.png",
    HuggingFace: "/ai-logos/huggingface.png",
    "Hugging Face": "/ai-logos/huggingface.png",
    Atlassian: "/settingspage/jira.png",
    Postman: "/inte/postman.png",
    SendGrid: "/inte/sendgrid.png",
    Slack: "/settingspage/slack.png",
    SlackWebhook: "/settingspage/slack.png",
  };

  // Try exact match first
  if (fallbackIconMap[providerName]) {
    return fallbackIconMap[providerName];
  }

  // Try case-insensitive match
  const lowerName = providerName.toLowerCase();
  for (const [key, icon] of Object.entries(fallbackIconMap)) {
    if (key.toLowerCase() === lowerName) {
      return icon;
    }
  }

  // Default fallback
  return "/github.png";
};

const VC_PROVIDERS = ["github", "gitlab", "bitbucket", "gitea"];
const STORAGE_PROVIDERS = ["s3", "gcs", "azure-blob", "gcp", "azure", "aws"];

// Color palette for pie chart slices
const COLORS = [
  "#7C3AED", // Purple
  "#A78BFA", // Light purple
  "#C4B5FD", // Lighter purple
  "#DDD6FE", // Very light purple
  "#EDE9FE", // Pale purple
  "#F5F3FF", // Extra pale purple
  "#8B5CF6", // Medium purple
  "#6D28D9", // Dark purple
];

// Static mock data - matches the expected API response format
const STATIC_HEATMAP_DATA = {
  // Version Control providers
  github: {
    "AWS Access Key": {
      count: 1850,
      data: {
        active: 1420,
        unvalidated: 430,
      },
    },
    "Slack Token": {
      count: 1360,
      data: {
        active: 1020,
        unvalidated: 340,
      },
    },
    "PostHog API Key": {
      count: 1070,
      data: {
        active: 810,
        unvalidated: 260,
      },
    },
    "GitHub Token": {
      count: 885,
      data: {
        active: 680,
        unvalidated: 205,
      },
    },
    "Datadog API Key": {
      count: 685,
      data: {
        active: 520,
        unvalidated: 165,
      },
    },
  },
  gitlab: {
    "AWS Access Key": {
      count: 1240,
      data: {
        active: 950,
        unvalidated: 290,
      },
    },
    "Stripe API Key": {
      count: 980,
      data: {
        active: 750,
        unvalidated: 230,
      },
    },
    "MongoDB Connection String": {
      count: 720,
      data: {
        active: 550,
        unvalidated: 170,
      },
    },
  },
  bitbucket: {
    "AWS Access Key": {
      count: 650,
      data: {
        active: 500,
        unvalidated: 150,
      },
    },
    "Redis Password": {
      count: 420,
      data: {
        active: 320,
        unvalidated: 100,
      },
    },
  },
  // Storage providers
  s3: {
    "AWS Access Key": {
      count: 2340,
      data: {
        active: 1890,
        unvalidated: 450,
      },
    },
    "Datadog API Key": {
      count: 1920,
      data: {
        active: 1520,
        unvalidated: 400,
      },
    },
    "Stripe API Key": {
      count: 1600,
      data: {
        active: 1250,
        unvalidated: 350,
      },
    },
    "MongoDB Connection String": {
      count: 1280,
      data: {
        active: 980,
        unvalidated: 300,
      },
    },
    "Postgres Connection String": {
      count: 1070,
      data: {
        active: 810,
        unvalidated: 260,
      },
    },
  },
  gcs: {
    "GCP Service Account": {
      count: 1850,
      data: {
        active: 1420,
        unvalidated: 430,
      },
    },
    "Google Cloud API Key": {
      count: 1420,
      data: {
        active: 1080,
        unvalidated: 340,
      },
    },
    "Firebase Token": {
      count: 980,
      data: {
        active: 750,
        unvalidated: 230,
      },
    },
  },
  "azure-blob": {
    "Azure Storage Key": {
      count: 1650,
      data: {
        active: 1270,
        unvalidated: 380,
      },
    },
    "Azure Service Principal": {
      count: 1200,
      data: {
        active: 920,
        unvalidated: 280,
      },
    },
  },
};

// Helper to transform old format data
const transformOldFormat = (obj) => {
  if (!obj || typeof obj !== "object") return [];
  const entries = Object.entries(obj).filter(
    ([key, value]) => value && typeof value === "object"
  );

  const items = [];

  for (const [provider, details] of entries) {
    const value = Number(details.count) || Number(details.sum) || 0;
    if (value > 0) {
      items.push({
        name: provider,
        value: value,
        active: Number(details.data?.active) || 0,
        risky: Number(details.data?.unvalidated) || 0,
      });
    }
  }
  return items;
};

// Aggregate providers data
const aggregateProviders = (data) => {
    const vcMap = new Map();
    const storageMap = new Map();

    const calculatePercentages = (map) => {
      const total = Array.from(map.values()).reduce(
        (sum, item) => sum + item.value,
        0
      );

      const allItems = Array.from(map.values())
        .map((item) => ({
          ...item,
          percentage: total > 0 ? (item.value / total) * 100 : 0,
        }))
        .sort((a, b) => b.percentage - a.percentage);

      const mainItems = [];
      const othersItems = [];

      allItems.forEach((item, index) => {
        if (index < 10 && item.percentage >= 1) {
          mainItems.push(item);
        } else {
          othersItems.push(item);
        }
      });

      if (othersItems.length === 0) {
        return mainItems;
      }

      const othersItem = {
        name: "Others",
        displayName: "Others",
        value: othersItems.reduce((sum, item) => sum + item.value, 0),
        active: othersItems.reduce((sum, item) => sum + item.active, 0),
        risky: othersItems.reduce((sum, item) => sum + item.risky, 0),
        breakdown: othersItems
          .map((item) => ({
            source: item.displayName || item.name,
            value: item.value,
            active: item.active,
            risky: item.risky,
          }))
          .sort((a, b) => b.value - a.value),
      };

      othersItem.percentage = total > 0 ? (othersItem.value / total) * 100 : 0;

      return [...mainItems, othersItem];
    };

    // Process data based on keys being providers (New Format)
    Object.entries(data).forEach(([providerName, providerData]) => {
      const normalizedProvider = normalizeProviderName(providerName);
      let targetMap = null;

      if (VC_PROVIDERS.includes(normalizedProvider)) {
        targetMap = vcMap;
      } else if (STORAGE_PROVIDERS.includes(normalizedProvider)) {
        targetMap = storageMap;
      }

      if (targetMap && providerData && typeof providerData === "object") {
        // Iterate over findings (secret types)
        Object.entries(providerData).forEach(([findingName, details]) => {
          const val = Number(details.count) || Number(details.sum) || 0;
          if (val > 0) {
            const active = Number(details.data?.active) || 0;
            const risky = Number(details.data?.unvalidated) || 0;

            if (targetMap.has(findingName)) {
              const existing = targetMap.get(findingName);
              existing.value += val;
              existing.active += active;
              existing.risky += risky;
              existing.breakdown.push({
                source: providerName,
                value: val,
                active: active,
                risky: risky,
              });
              existing.breakdown.sort((a, b) => b.value - a.value);
            } else {
              targetMap.set(findingName, {
                name: findingName,
                displayName: findingName,
                value: val,
                active: active,
                risky: risky,
                breakdown: [
                  {
                    source: providerName,
                    value: val,
                    active: active,
                    risky: risky,
                  },
                ],
              });
            }
          }
        });
      }
    });

    // Fallback logic for old format if maps are empty
    if (vcMap.size === 0 && storageMap.size === 0) {
      const findingDetails = data.finding_details || {};
      const s3Findings = data.s3_findings || {};
      const azureFindings = data.storage_azure || {};

      const processOldItems = (items, sourceName) => {
        items.forEach((item) => {
          const normalized = normalizeProviderName(item.name);
          let targetMap = null;
          if (VC_PROVIDERS.includes(normalized)) targetMap = vcMap;
          else if (STORAGE_PROVIDERS.includes(normalized))
            targetMap = storageMap;

          if (targetMap) {
            if (targetMap.has(normalized)) {
              const existing = targetMap.get(normalized);
              existing.value += item.value;
              existing.active += item.active;
              existing.risky += item.risky;
              existing.breakdown.push({
                source: sourceName,
                value: item.value,
                active: item.active,
                risky: item.risky,
              });
              existing.breakdown.sort((a, b) => b.value - a.value);
            } else {
              targetMap.set(normalized, {
                name: normalized,
                displayName: item.name,
                value: item.value,
                active: item.active,
                risky: item.risky,
                breakdown: [
                  {
                    source: sourceName,
                    value: item.value,
                    active: item.active,
                    risky: item.risky,
                  },
                ],
              });
            }
          }
        });
      };

      processOldItems(transformOldFormat(findingDetails), "finding_details");
      processOldItems(transformOldFormat(s3Findings), "s3_findings");
      processOldItems(transformOldFormat(azureFindings), "storage_azure");
    }

  return {
    vc: calculatePercentages(vcMap),
    storage: calculatePercentages(storageMap),
  };
};

const CollapsibleLegend = ({ data, activeIndex, onHover }) => {
  const [isOpen, setIsOpen] = useState(true);

  if (!data || data.length === 0) return null;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="w-full mt-4 border-t border-gray-100 pt-4"
    >
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">Legend</h4>
        <CollapsibleTrigger asChild>
          <button className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <ChevronDown
              className={cn(
                "h-4 w-4 text-gray-500 transition-transform duration-200",
                isOpen ? "transform rotate-180" : ""
              )}
            />
          </button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent className="mt-3">
        <div className="flex flex-wrap gap-2">
          {data.map((entry, index) => (
            <div
              key={`legend-${index}`}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs cursor-pointer transition-colors",
                activeIndex === index
                  ? "bg-gray-100 border-gray-300"
                  : "bg-white border-gray-100 hover:border-gray-200"
              )}
              onMouseEnter={() => onHover(index)}
              onMouseLeave={() => onHover(null)}
            >
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <img
                src={getProviderIcon(entry.name)}
                alt={entry.name}
                className="w-4 h-4 object-contain"
              />
              <span className="font-medium text-gray-700 truncate max-w-[100px]">
                {entry.name}
              </span>
              <span className="text-gray-500 ml-1">{entry.value}</span>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const HeatMap = () => {
  const isMobile = useIsMobile();
  const [activeVcIndex, setActiveVcIndex] = useState(null);
  const [activeStorageIndex, setActiveStorageIndex] = useState(null);

  // Compute data from static source using useMemo
  const { vc: vcData, storage: storageData } = useMemo(
    () => aggregateProviders(STATIC_HEATMAP_DATA),
    []
  );

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
    percentage,
  }) => {
    if (isMobile) return null;
    if (percentage < 1) return null;

    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#2B1E5A"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        className="font-sans text-xs font-medium"
      >
        {name}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-[#E5DDFB] shadow-[0_8px_24px_rgba(43,30,90,0.15)] rounded-xl p-3 min-w-[180px]">
          <div className="font-sans font-bold text-xs mb-1.5 text-[#2B1E5A] flex items-center justify-between">
            <span className="capitalize">{data.name}</span>
            <span>{`${Number(data.percentage).toFixed(1)}%`}</span>
          </div>
          <div className="font-sans text-xs text-[#33275E]">
            <div className="flex justify-between gap-3">
              <span>Tokens Found</span>
              <span className="font-semibold">{data.value}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span>Active</span>
              <span className="font-semibold">{data.active}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span>Risky</span>
              <span className="font-semibold">{data.risky}</span>
            </div>
            {data.breakdown &&
              (data.breakdown.length > 1 || data.name === "Others") && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="text-[10px] font-semibold mb-1 text-gray-500">
                    Source Breakdown
                  </div>
                  {data.breakdown.map((b, i) => (
                    <div
                      key={i}
                      className="flex justify-between text-[10px] text-gray-600 mb-0.5"
                    >
                      <span className="truncate max-w-[100px]">{b.source}</span>
                      <span>{b.value}</span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <TooltipProvider>
      <div
        className={cn(
          "grid gap-4",
          isMobile ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"
        )}
      >
        {/* Version Control Card */}
        <Card
          className={cn(
            "bg-white border-[#CBC8C89C] relative",
            isMobile ? "rounded-xl p-4" : "rounded-2xl p-5"
          )}
        >
          <CardContent className="p-0">
            <div
              className={cn(
                "flex justify-between items-center",
                isMobile ? "flex-col gap-2 mb-2" : "flex-row mb-3"
              )}
            >
              <div className="flex items-center gap-2">
                <h2
                  className={cn(
                    "font-semibold text-black font-sans tracking-tight",
                    isMobile ? "text-sm" : "text-lg"
                  )}
                >
                  Version Control
                </h2>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={`w-5 h-5 rounded-full border border-black/35 bg-white flex items-center justify-center text-black/35 hover:bg-gray-50 transition-colors ${
                        isMobile ? "text-xs" : "text-sm"
                      }`}
                    >
                      <span className="font-semibold">i</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Token distribution for Version Control providers</p>
                  </TooltipContent>
                </UITooltip>
              </div>
            </div>

            <div>
              {vcData.length > 0 ? (
                <>
                  <ResponsiveContainer
                    width="100%"
                    height={isMobile ? 250 : 320}
                  >
                    <PieChart>
                      <Pie
                        data={vcData}
                        cx="50%"
                        cy="50%"
                        labelLine={!isMobile}
                        label={renderCustomLabel}
                        outerRadius={isMobile ? 75 : 100}
                        fill="#8884d8"
                        dataKey="value"
                        isAnimationActive={false}
                        onMouseEnter={(_, index) => setActiveVcIndex(index)}
                        onMouseLeave={() => setActiveVcIndex(null)}
                      >
                        {vcData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                            strokeWidth={activeVcIndex === index ? 3 : 1}
                            stroke={
                              activeVcIndex === index ? "#2B1E5A" : "#fff"
                            }
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "transparent" }}
                        wrapperStyle={{ zIndex: 1000 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <CollapsibleLegend
                    data={vcData}
                    activeIndex={activeVcIndex}
                    onHover={setActiveVcIndex}
                  />
                </>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-sm text-gray-500">
                  No data
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Storage Card */}
        <Card
          className={cn(
            "bg-white border-[#CBC8C89C] relative",
            isMobile ? "rounded-xl p-4" : "rounded-2xl p-5"
          )}
        >
          <CardContent className="p-0">
            <div
              className={cn(
                "flex justify-between items-center",
                isMobile ? "flex-col gap-2 mb-2" : "flex-row mb-3"
              )}
            >
              <div className="flex items-center gap-2">
                <h2
                  className={cn(
                    "font-semibold text-black font-sans tracking-tight",
                    isMobile ? "text-sm" : "text-lg"
                  )}
                >
                  Storage Providers
                </h2>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={`w-5 h-5 rounded-full border border-black/35 bg-white flex items-center justify-center text-black/35 hover:bg-gray-50 transition-colors ${
                        isMobile ? "text-xs" : "text-sm"
                      }`}
                    >
                      <span className="font-semibold">i</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Token distribution for Storage providers</p>
                  </TooltipContent>
                </UITooltip>
              </div>
            </div>

            <div>
              {storageData.length > 0 ? (
                <>
                  <ResponsiveContainer
                    width="100%"
                    height={isMobile ? 250 : 320}
                  >
                    <PieChart>
                      <Pie
                        data={storageData}
                        cx="50%"
                        cy="50%"
                        labelLine={!isMobile}
                        label={renderCustomLabel}
                        outerRadius={isMobile ? 75 : 100}
                        fill="#8884d8"
                        dataKey="value"
                        isAnimationActive={false}
                        onMouseEnter={(_, index) =>
                          setActiveStorageIndex(index)
                        }
                        onMouseLeave={() => setActiveStorageIndex(null)}
                      >
                        {storageData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                            strokeWidth={activeStorageIndex === index ? 3 : 1}
                            stroke={
                              activeStorageIndex === index ? "#2B1E5A" : "#fff"
                            }
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "transparent" }}
                        wrapperStyle={{ zIndex: 1000 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <CollapsibleLegend
                    data={storageData}
                    activeIndex={activeStorageIndex}
                    onHover={setActiveStorageIndex}
                  />
                </>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-sm text-gray-500">
                  No data
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default HeatMap;
